import { FC, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'src/app/reduxCustomHooks';
import { useEditPageTask } from 'src/components/hooks/useEditPageTask';
import { useTaskOutputActions } from 'src/components/hooks/useTaskOutputActions';
import { isOpenableTaskState } from 'src/utils/taskProgressStreamUtils';
import { useDemoFirstUploadTrim } from 'src/components/hooks/useDemoFirstUploadTrim';
import { useDemoLeadCaptureEnabled } from 'src/components/hooks/useDemoLeadCaptureEnabled';
import { DemoLeadCapture } from './DemoLeadCapture';
import { DemoFreeMonthDialog } from './DemoFreeMonthDialog';
import { freeMonthLocale, freeMonthStartUrl } from 'src/utils/freeMonth';
import { sendEditorMetric } from 'src/utils/editorMetrics';
import { useEditorTipMetric } from 'src/components/hooks/useEditorMetrics';
import { DEMO_TIP_ATTR } from 'src/utils/demoTipHighlight';
import type { EditPageUndoAction } from 'src/app/slices/editPageSlice';

export const DEMO_HINT_DISMISSED_KEY = 'anonify.demoHint.anonymizeDismissed';
export const DEMO_NEXT_STEP_DISMISSED_KEY = 'anonify.demoNextStep.dismissed';

// A boolean only; storage that is blocked just means the panel shows again.
const readFlag = (key: string, storage: Storage) => {
  try {
    return storage.getItem(key) === '1';
  } catch {
    return false;
  }
};
const writeFlag = (key: string, storage: Storage) => {
  try {
    storage.setItem(key, '1');
  } catch {
    /* ignore */
  }
};

// Left edge of the bubble on desktop: just right of the 88px sidebar
// (sidebarMinWidth in DashboardTemplate; not imported, that would be circular).
const BUBBLE_LEFT_MD = '100px';

/**
 * Speech-bubble callout, fixed at the bottom edge of the viewport (always
 * inside it, never in the page flow, so it needs no scrolling). It sits below
 * the app bar (z-index), so the toolbar and "Anonymize document" stay free;
 * only the empty gutter and the bottom of the pages can be under it, and it
 * closes. The wrapper ignores the pointer, so only the bubble takes clicks.
 */
const Dock: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      position: 'fixed',
      zIndex: (theme) => theme.zIndex.speedDial,
      bottom: { xs: 16, md: 20 },
      left: { xs: 12, md: BUBBLE_LEFT_MD },
      // Room for the help button at the bottom right.
      right: { xs: 80, md: 'auto' },
      maxWidth: { md: 'min(560px, calc(100vw - 260px))' },
      pointerEvents: 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 0.75,
    }}
  >
    {children}
  </Box>
);

/**
 * The one-line page-limit note: a slim pill above the bubble, in the same
 * fixed dock (so it never overlaps the bubble), not dismissible: it stays
 * while the demo document was trimmed. The text is the demo banner's own key.
 */
const PageLimitNote: FC<{ used: number; total: number }> = ({ used, total }) => {
  const { t } = useTranslation();
  return (
    <Typography
      variant="body2"
      role="status"
      data-testid="demo-page-limit-notice"
      sx={{
        pointerEvents: 'auto',
        px: 1.5,
        py: 0.25,
        borderRadius: 3,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        fontSize: '0.75rem',
        fontWeight: 600,
      }}
    >
      {t('demoPageLimitNotice', { used, total })}
    </Typography>
  );
};

const Bubble: FC<{
  testId: string;
  dismissLabel: string;
  dismissTestId: string;
  onDismiss: () => void;
  note?: React.ReactNode;
  // Step flow: "Next"/"Done" and "1/3" replace the close cross while a tip shows.
  steps?: { label: string; stepLabel: string; step: string; onNext: () => void };
  children: React.ReactNode;
}> = ({ testId, dismissLabel, dismissTestId, onDismiss, note, steps, children }) => (
  <Dock>
    {note}
    <Paper
      role="status"
      elevation={12}
      data-testid={testId}
      sx={{
        position: 'relative',
        pointerEvents: 'auto',
        maxHeight: 'calc(100vh - 200px)',
        '@supports (height: 100dvh)': { maxHeight: 'calc(100dvh - 200px)' },
        overflowY: 'auto',
        border: 2,
        borderColor: 'primary.main',
        // Light ring + shadow so the bubble separates from a dark page.
        boxShadow: (theme) =>
          `0 0 0 3px ${alpha(theme.palette.primary.main, 0.35)}, 0 8px 28px rgba(0,0,0,0.6)`,
        borderRadius: 3,
        px: 1.5,
        py: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        '& .MuiTypography-body2': { fontSize: '0.8125rem' },
        '@media (prefers-reduced-motion: no-preference)': {
          animation: 'demoBubbleIn 220ms ease-out',
        },
        '@keyframes demoBubbleIn': {
          from: { opacity: 0, transform: 'translateY(16px)' },
          to: { opacity: 1, transform: 'none' },
        },
      }}
    >
      <Box sx={{ minWidth: 0, pr: steps ? 0 : 4 }}>{children}</Box>
      {steps ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="medium"
            endIcon={<ArrowForwardIcon />}
            onClick={steps.onNext}
            data-testid="demo-tip-next"
            sx={{
              minHeight: 44,
              '&.Mui-focusVisible, &:focus-visible': {
                outline: '2px solid',
                outlineColor: 'common.white',
                outlineOffset: 2,
              },
            }}
          >
            {steps.label}
          </Button>
          <Typography
            variant="body2"
            component="span"
            aria-label={steps.stepLabel}
            data-testid="demo-tip-step"
            sx={{ fontWeight: 600 }}
          >
            {steps.step}
          </Typography>
          <Button
            size="small"
            color="inherit"
            onClick={onDismiss}
            data-testid={dismissTestId}
            aria-label={dismissLabel}
            sx={{ ml: 'auto', opacity: 0.75, minHeight: 32 }}
          >
            {dismissLabel}
          </Button>
        </Box>
      ) : (
        <IconButton
          size="small"
          aria-label={dismissLabel}
          onClick={onDismiss}
          data-testid={dismissTestId}
          sx={{ position: 'absolute', top: 4, right: 4 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Paper>
  </Dock>
);

/** The new tips, in the order they are offered (one at a time). */
export type DemoTip = 'boxes' | 'add_remove' | 'review';

const ADD_ACTIONS: EditPageUndoAction['type'][] = [
  'ADD_ITEM',
  'ADD_CUSTOM_BOX',
];
const historyOf = (editPage: {
  undoStack?: EditPageUndoAction[];
  redoStack?: EditPageUndoAction[];
}) => [...(editPage.undoStack ?? []), ...(editPage.redoStack ?? [])];

/**
 * What the visitor did with the boxes of the routed document, from the undo
 * and redo history (an undone edit moves to redo, so both stacks together
 * only grow until the document is left). "Clicked a box" means an edit of an
 * existing box (redact state, delete, legal code, a "not sure" decision); the
 * box popover itself lives in the editor iframe and sends nothing back.
 */
const useBoxProgress = () => {
  // Booleans only, so the panel does not re-render on every edit.
  const added = useAppSelector((state) =>
    historyOf(state.editPage).some((a) => ADD_ACTIONS.includes(a.type))
  );
  const boxEdited = useAppSelector((state) =>
    historyOf(state.editPage).some((a) => !ADD_ACTIONS.includes(a.type))
  );
  const markerSelected = useAppSelector(
    (state) => !!state.editPage.selectedMarkerId
  );
  const taskId = useAppSelector((state) => state.editPage.routeTaskId ?? null);
  // Opening a "not sure" box popover is a box click too; it is transient in
  // the slice, so remember it for this document (in memory).
  const [markerClickedIn, setMarkerClickedIn] = useState<string | null>(null);
  useEffect(() => {
    if (markerSelected) setMarkerClickedIn(taskId);
  }, [markerSelected, taskId]);
  const boxClicked =
    boxEdited || (markerClickedIn !== null && markerClickedIn === taskId);
  return { edited: added || boxClicked, added, boxClicked };
};

/**
 * One small speech bubble for anonymous demo users in the editor, fixed at
 * the bottom edge of the viewport (see Bubble), shown as soon as the first
 * suggestions have loaded (or the export exists). Pricing and "book a demo"
 * live in the demo account banner at the top, not here.
 * - Tips, one at a time: `boxes` (results shown, no box edited yet), then
 *   `add_remove` (after `boxes` is closed or a box was edited, until
 *   something is added; desktop only, where the toolbar has the selection
 *   tools), then `review`. Each can be closed; that is kept in memory only
 *   (no storage, no cookie), so a reload shows them again.
 * - Until the export starts, a line says where the redacted PDF comes from
 *   (the download only exists after "Anonymize document"). Starting the
 *   export, or closing the hint itself, keeps that line away on later loads
 *   (localStorage).
 * - After a successful export, in the UI locales the backend allows (NL, EN;
 *   not DE yet), an optional contact email (DemoLeadCapture); it never gates
 *   anything. Closing it keeps it away for this tab (sessionStorage,
 *   DEMO_NEXT_STEP_DISMISSED_KEY: the key of the former next-step panel).
 * - Free-month variant: when REACT_APP_FREE_MONTH_URL is a valid URL and the
 *   UI language is NL or EN, a modal (DemoFreeMonthDialog) replaces that
 *   inline ask, once per tab under the same key. Its fields stay in the
 *   browser; see utils/freeMonth. Unset, invalid or any other language: the
 *   inline ask above, unchanged.
 * Every tip, and the export hint as `export_hint`, sends `editor_tip`
 * shown/clicked/dismissed once per task view (useEditorTipMetric; nothing
 * under DNT/GPC). The caller renders this for demo users only.
 */
const DemoEditorGuidanceInner: FC = () => {
  const { t, i18n } = useTranslation();
  const { canDownload, isGenerating, downloadDocument } =
    useTaskOutputActions();
  const taskState = useEditPageTask((task) => task?.state);
  const canEdit = isOpenableTaskState(taskState);
  const hasSuggestions = useAppSelector((state) =>
    state.editPage.pages.some((page) => !!page?.length)
  );
  // Optional end-of-demo email under the CTA, per UI locale.
  const leadCaptureEnabled = useDemoLeadCaptureEnabled();
  const trim = useDemoFirstUploadTrim();

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const trackTip = useEditorTipMetric();
  const { edited, added, boxClicked } = useBoxProgress();
  // In memory only: closing a tip never touches storage or cookies.
  const [closedTips, setClosedTips] = useState<DemoTip[]>([]);

  const [hintDismissed, setHintDismissed] = useState(() =>
    readFlag(DEMO_HINT_DISMISSED_KEY, localStorage)
  );
  const [nextDismissed, setNextDismissed] = useState(() =>
    readFlag(DEMO_NEXT_STEP_DISMISSED_KEY, sessionStorage)
  );

  // Starting the export answers the hint.
  const exportStarted = isGenerating || canDownload;
  useEffect(() => {
    if (exportStarted && !hintDismissed) {
      writeFlag(DEMO_HINT_DISMISSED_KEY, localStorage);
      setHintDismissed(true);
    }
  }, [exportStarted, hintDismissed]);

  const visible = canEdit && (hasSuggestions || canDownload);
  // The optional email: only after a successful export, once, per tab.
  const askEmail = visible && leadCaptureEnabled && canDownload && !nextDismissed;
  // Free-month pop-up instead of the inline ask (off unless the build sets the URL).
  const freeMonthUrl = freeMonthStartUrl();
  const fmLocale = freeMonthLocale(i18n.language);
  const freeMonthOn = !!freeMonthUrl && !!fmLocale;
  const freeMonthOpen =
    freeMonthOn && visible && canDownload && !isGenerating && !nextDismissed;
  const freeMonthShown = useRef(false);
  useEffect(() => {
    if (!freeMonthOpen || freeMonthShown.current) return;
    freeMonthShown.current = true;
    // Existing cookieless counter; carries no field.
    sendEditorMetric('lead_capture_shown');
  }, [freeMonthOpen]);
  // Once: under the last tip, or alone when no tip is on screen.
  const showHint = !hintDismissed && !exportStarted;
  const isClosed = (tip: DemoTip) => closedTips.includes(tip);
  // One tip at a time, in order: boxes -> add_remove -> review.
  const boxesActive = hasSuggestions && !edited && !isClosed('boxes');
  const addRemoveActive =
    isDesktop &&
    (isClosed('boxes') || boxClicked || !hasSuggestions) &&
    !added &&
    !isClosed('add_remove');
  const tip: DemoTip | null = !visible
    ? null
    : boxesActive
    ? 'boxes'
    : addRemoveActive
    ? 'add_remove'
    : !isClosed('review')
    ? 'review'
    : null;
  // The hint is on screen in both panels (next step, or on its own).
  const hintOnScreen = visible && showHint && (tip === null || tip === 'review');
  // The labelled download (same handler and label as the toolbar icon): like
  // the export hint, only under the last tip, or alone when no tip is on screen.
  const showDownload =
    visible && canDownload && !nextDismissed && (tip === null || tip === 'review');

  useEffect(() => {
    if (tip) trackTip(tip, 'shown');
  }, [tip, trackTip]);
  // Tells the toolbar which tip is active (see demoTipHighlightSx).
  useEffect(() => {
    if (tip !== 'add_remove') return;
    document.body.setAttribute(DEMO_TIP_ATTR, tip);
    return () => document.body.removeAttribute(DEMO_TIP_ATTR);
  }, [tip]);
  useEffect(() => {
    if (hintOnScreen) trackTip('export_hint', 'shown');
  }, [hintOnScreen, trackTip]);

  // Shown whenever the document can be edited (also before suggestions load).
  const note =
    canEdit && trim ? (
      <PageLimitNote used={trim.usedPages} total={trim.totalPages} />
    ) : null;

  if (!visible) return note && <Dock>{note}</Dock>;

  const dismissHint = () => {
    writeFlag(DEMO_HINT_DISMISSED_KEY, localStorage);
    setHintDismissed(true);
    trackTip('export_hint', 'dismissed');
  };
  const closeTip = (closed: DemoTip) => {
    setClosedTips((list) => (list.includes(closed) ? list : [...list, closed]));
    trackTip(closed, 'dismissed');
  };
  const hint = (
    <Typography variant="body2" data-testid="demo-anonymize-hint">
      {t('demoHint.anonymize', { action: t('generateDoc') })}
    </Typography>
  );
  const tipText = (current: DemoTip) =>
    current === 'boxes'
      ? t('demoTip.boxes')
      : current === 'add_remove'
      ? t('demoTip.addRemove', {
          textSelection: t('editToolbar.textSelection'),
          areaSelection: t('editToolbar.areaSelection'),
        })
      : t('demoTip.review');
  const tipBlock = (current: DemoTip) => (
    <Box data-testid={`demo-tip-${current}`}>
      <Typography variant="body2">{tipText(current)}</Typography>
    </Box>
  );
  const order: DemoTip[] = isDesktop
    ? ['boxes', 'add_remove', 'review']
    : ['boxes', 'review'];
  const stepOf = (current: DemoTip) => ({
    current: order.indexOf(current) + 1,
    total: order.length,
  });
  const skipAll = (current: DemoTip) => {
    setClosedTips(['boxes', 'add_remove', 'review']);
    trackTip(current, 'dismissed');
  };

  // One bubble, one close button: while a tip shows, its close is the only
  // one; then the export hint's; then (after an export) the email ask's.
  const dismiss = tip
    ? {
        label: t('demoTip.dismiss'),
        testId: `demo-tip-${tip}-dismiss`,
        onClick: () => skipAll(tip),
      }
    : showHint
    ? {
        label: t('demoHint.dismiss'),
        testId: 'demo-anonymize-hint-dismiss',
        onClick: dismissHint,
      }
    : {
        label: t('demoNextStep.dismiss'),
        testId: 'demo-post-export-dismiss',
        onClick: () => {
          writeFlag(DEMO_NEXT_STEP_DISMISSED_KEY, sessionStorage);
          setNextDismissed(true);
        },
      };
  if (!tip && !hintOnScreen && !askEmail && !showDownload)
    return note && <Dock>{note}</Dock>;
  return (
    <>
    <Bubble
      testId="demo-guidance"
      dismissLabel={dismiss.label as string}
      dismissTestId={dismiss.testId}
      onDismiss={dismiss.onClick}
      steps={
        tip
          ? {
              label: (tip === 'review' ? t('demoTip.done') : t('demoTip.next')) as string,
              step: t('demoTip.step', stepOf(tip)) as string,
              stepLabel: t('demoTip.step', stepOf(tip)) as string,
              onNext: () => closeTip(tip),
            }
          : undefined
      }
      note={note}
    >
      {tip && tipBlock(tip)}
      {hintOnScreen && <Box sx={{ mt: tip ? 0.75 : 0 }}>{hint}</Box>}
      {showDownload && (
        <Button
          size="small"
          variant={tip ? 'outlined' : 'contained'}
          startIcon={<DownloadIcon />}
          onClick={downloadDocument}
          data-testid="demo-download-button"
          sx={{ mt: tip || hintOnScreen ? 0.75 : 0 }}
        >
          {t('download')}
        </Button>
      )}
      {/* canDownload: output state GENERATED_OUTPUT, i.e. an export finished
          successfully (failed exports reset to NOT_GENERATED). */}
      {!freeMonthOn && askEmail && <DemoLeadCapture />}
    </Bubble>
    {freeMonthOn && (
      <DemoFreeMonthDialog
        open={freeMonthOpen}
        onClose={() => {
          writeFlag(DEMO_NEXT_STEP_DISMISSED_KEY, sessionStorage);
          setNextDismissed(true);
        }}
        startUrl={freeMonthUrl as string}
        locale={fmLocale as 'nl' | 'en'}
      />
    )}
    </>
  );
};

/** One guidance per document: a new task id starts with all tips open again. */
export const DemoEditorGuidance: FC = () => {
  const taskId = useAppSelector((state) => state.editPage.routeTaskId ?? null);
  return <DemoEditorGuidanceInner key={taskId ?? 'none'} />;
};
